import './telemetry';
import 'react-medium-image-zoom/dist/styles.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { datadogConfig, dash0Config, reactQueryConfig, recaptchaConfig } from '@api/auth/config';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { ApiError } from '@api/errors';
import { router } from './router';
import { getLogger, setLogger, type Logger } from '@logging/logger';
import { createConsoleLogger } from '@logging/console';
import { createDatadogLogger } from '@logging/datadog';
import { createOtelLogger } from '@logging/otel';
import { createCompositeLogger } from '@logging/composite';
import { Heap } from '@components/HeapEvents';
import { LaunchDarklyWrapper } from './LaunchDarklyWrapper';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';

{
  const active = [
    datadogConfig.enabled ? createDatadogLogger(datadogConfig) : null,
    dash0Config.enabled ? createOtelLogger(dash0Config) : null,
  ].filter((l): l is Logger => l !== null);
  setLogger(active.length > 1 ? createCompositeLogger(...active) : (active[0] ?? createConsoleLogger()));
}

function logQueryFailure(scope: 'query' | 'mutation', key: unknown, err: unknown): void {
  const status = err instanceof ApiError ? err.status : undefined;
  const level: 'warn' | 'error' = status && [401, 403, 404].includes(status) ? 'warn' : 'error';
  const detail = err instanceof Error && err.message ? `: ${err.message}` : '';
  getLogger()[level](`${scope} failed${detail}`, { [`${scope}Key`]: key, status }, err);
}

const heapEnvId = import.meta.env.PUBLIC_HEAP_ENV_ID;
if (heapEnvId && typeof window !== 'undefined' && window.heap?.load) {
  window.heap.load(heapEnvId);
}

const muiCache = createCache({ key: 'mui', prepend: true });
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err, query) => {
      logQueryFailure('query', query.queryKey, err);
      if (err instanceof ApiError && err.status === 403) {
        router.navigate({ to: '/forbidden' });
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (err, _vars, _ctx, mutation) => logQueryFailure('mutation', mutation.options.mutationKey, err),
  }),
  defaultOptions: {
    queries: {
      staleTime: reactQueryConfig.staleTime,
      retry: reactQueryConfig.retry,
      throwOnError: (err) => {
        const handledByInterceptor = err instanceof ApiError && [401, 403, 404, 500, 503].includes(err.status);
        return !handledByInterceptor;
      },
    },
  },
});

const rootEl = document.getElementById('root');
if (rootEl) {
  const root = ReactDOM.createRoot(rootEl);
  root.render(
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Heap />
        <CacheProvider value={muiCache}>
          <QueryClientProvider client={queryClient}>
            <GoogleReCaptchaProvider reCaptchaKey={recaptchaConfig.siteKey ?? ''}>
              <LaunchDarklyWrapper>
                <App />
              </LaunchDarklyWrapper>
            </GoogleReCaptchaProvider>
          </QueryClientProvider>
        </CacheProvider>
      </ThemeProvider>
    </React.StrictMode>,
  );
}
